import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, onMounted, watch, onUnmounted } from 'vue'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { geoEqualEarth } from 'd3-geo'
import * as THREE from 'three'
import { GeoUtil, LatLong } from '@/util/GeoUtil';

const FLAT_MIN_ZOOM = 1.1 // flat geometry width (2.0) fills camera frustum (±1.1)

interface Marker {
  id: string
  location: LatLong
  mesh: THREE.Mesh
}

interface FlagSprite {
  code: string
  location: LatLong
  sprite: THREE.Sprite
}

interface LongitudeLine {
  baseLon: number
  line: THREE.Line
  normalOpacity: number
}

interface LatitudeLine {
  baseLat: number
  line: THREE.LineLoop
  normalOpacity: number
}

const FLAT_MAP_HALF_HEIGHT = 0.52 // Equal Earth projection half-height at scale 0.4


export default /*#__PURE__*/_defineComponent({
  __name: 'Earth',
  props: {
    mode: {}
  },
  emits: [
  'update:mode'
],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





__expose({
  moveTo,
  addMarker,
  removeMarker,
  clearMarkers: () => markers.length = 0,
  showMarker,
  moveToMarker,
  addCountryFlag,
  clearCountryFlags: () => flags.clear(),
})

const earthElemRef = ref<HTMLDivElement>()
const earthLoadedRef = ref(false)
const enableControls = ref(true)
const morphValueRef = ref(getModeMorphValue(props.mode))

const earthTexture = new URL('@/assets/earth/earth-texture.jpg', import.meta.url).href

const flagContext = require.context('flag-icons/flags/4x3', false, /\.svg$/)
const flagModules: Record<string, string> = {}
flagContext.keys().forEach((key) => {
  const countryCode = key.replace('./', '').replace('.svg', '').toUpperCase()
  flagModules[countryCode] = flagContext(key)
});

// set up scene
const scene = new THREE.Scene()
const ambientLight = new THREE.AmbientLight(0xffffff, 0.4)
const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5)
scene.add(ambientLight)
scene.add(directionalLight)

const renderer = new THREE.WebGLRenderer({ antialias: true })
renderer.setClearColor(0x00000000, 0)

const camera = new THREE.OrthographicCamera(
  -1.1, 1.1, // left, right
  1.1, -1.1, // top, bottom
  -2, 2 // near, far (extra room for transitions)
) // 10% padding for markers
camera.lookAt(0, 0, 0)

const globeCameraPosition = new THREE.Spherical(1, Math.PI / 2, 0) // look at prime meridian
const flatCameraPosition = new THREE.Spherical(1, Math.PI / 2, 0)

let centerLongitude = 0
let flatPanY = 0
let activePointerId: number | null = null
let lastPointerX = 0
let lastPointerY = 0
let lastZoom = 1.0

// geometry for each mode
const globeGeometry = new THREE.SphereGeometry(1, 64, 32)
const flatGeometry = createFlatGeometry()

let controls: OrbitControls
let earthMesh: THREE.Mesh

let mainAnimationFrameId: number

const markers: Marker[] = []

let markerRadius = 0.05

const flags = new Map<string, FlagSprite>()

const latitudeLines: LatitudeLine[] = []
const longitudeLines: LongitudeLine[] = []

onMounted(async () => {
  const container = earthElemRef.value!
  renderer.setSize(container.clientWidth, container.clientHeight)
  container.appendChild(renderer.domElement)

  // create controls linked to camera and renderer element
  controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.04
  controls.rotateSpeed = 0.4

  // update based on current mode (no transition)
  updateControlsBehavior()
  updateZoom(getModeZoomTarget(props.mode), getModeZoomTarget(props.mode), 1.0)
  morphValueRef.value = getModeMorphValue(props.mode)
  updateCamera(globeCameraPosition, flatCameraPosition)
  updateLighting()
  updateGeometry()
  updateControls()

  // start animation loop
  const animate = () => {
    mainAnimationFrameId = requestAnimationFrame(animate)

    if (enableControls.value) {
      controls.update()
    }

    if (props.mode == 'flat' && camera.zoom !== lastZoom) {
      lastZoom = camera.zoom
      clampFlatPanY()
      applyFlatPanY()
    }

    // move light with camera
    const lightPosition = new THREE.Vector3(1, 1, 0.5) // relative to camera
    camera.localToWorld(lightPosition)
    directionalLight.position.copy(lightPosition)

    renderer.render(scene, camera)
  }
  animate()

  await addEarthAsync()

  const clientSize = Math.min(container.clientWidth, container.clientHeight)
  markerRadius = clientSize < 200 ? 0.05 : 0.02 // smaller earth, larger markers
})

onUnmounted(() => {
  cancelAnimationFrame(mainAnimationFrameId)
  controls.dispose()
})

function getModeMorphValue(mode: 'globe' | 'flat') {
  return mode == 'globe' ? 0 : 1
}

function getModeZoomTarget(mode: 'globe' | 'flat') {
  return mode == 'flat' ? FLAT_MIN_ZOOM : 1.0
}

watch(() => props.mode, (newMode, oldMode) => {
  if (newMode == 'flat') {
    // Set center meridian to match the globe's current camera longitude
    const currentSpherical = new THREE.Spherical().setFromVector3(camera.position)
    centerLongitude = THREE.MathUtils.radToDeg(currentSpherical.theta) - 90
    rebuildFlatProjection()
  } else {
    // Reset vertical pan when going back to globe
    flatPanY = 0
    controls.target.y = 0
  }

  // setup zoom transition
  const zoomStart = camera.zoom
  const zoomDuration = 800

  // setup morph transition
  const morphStart = getModeMorphValue(oldMode)
  const morphTarget = getModeMorphValue(newMode)
  const morphDuration = 800

  // setup camera transition
  const cameraStart = new THREE.Spherical().setFromVector3(camera.position)
  const cameraTarget = newMode == 'flat' ? flatCameraPosition : cameraStart

  // update controls behavior immediately
  updateControlsBehavior()

  const startTime = performance.now()

  function animate() {
    const elapsed = performance.now() - startTime

    const zoomT = Math.min(elapsed / zoomDuration, 1.0)
    updateZoom(zoomStart, getModeZoomTarget(newMode), zoomT)
    
    const morphDelay = zoomDuration * 0.7 // zoom mostly done
    const morphT = Math.min((elapsed - morphDelay) / morphDuration, 1.0)
    morphValueRef.value = THREE.MathUtils.lerp(morphStart, morphTarget, morphT)
    updateCamera(cameraStart, cameraTarget)
    updateLighting()
    updateGeometry()
    updateControls()

    for (const m of markers) {
      updateMarker(m)
    }
    for (const flag of flags.values()) {
      updateFlagPosition(flag)
    }
    updateLongitudeLinePositions()
    updateLatitudeLinePositions()
    updateGridLineOpacity()

    if (morphValueRef.value == morphTarget) {
      console.log('mode morph complete')
    }
    else {
      requestAnimationFrame(animate)
    } 
  }
  
  animate()
})

function updateZoom(start: number, target: number, t: number) {
  const easedT = THREE.MathUtils.smoothstep(t, 0, 1)
  camera.zoom = THREE.MathUtils.lerp(start, target, easedT)
  camera.updateProjectionMatrix()
}

function updateCamera(start: THREE.Spherical, target: THREE.Spherical) {
  const easedT = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)
  const newPosition = new THREE.Spherical(
    1,
    THREE.MathUtils.lerp(start.phi, target.phi, easedT),
    THREE.MathUtils.lerp(start.theta, target.theta, easedT)
  )
  camera.position.setFromSpherical(newPosition)
}

function updateLighting() {
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)
  ambientLight.intensity = THREE.MathUtils.lerp(0.5, 1.0, easedValue)
  directionalLight.intensity = THREE.MathUtils.lerp(1.0, 0.0, easedValue)
}

function updateGeometry() {
  if (!earthMesh)
    return

  const positions = earthMesh.geometry.attributes.position
  
  const itemSize = 3
  const newPositions = new Float32Array(positions.count * itemSize)
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)

  for (let i = 0; i < positions.count * itemSize; i++) {
    newPositions[i] = THREE.MathUtils.lerp(
      globeGeometry.attributes.position.array[i], // when value = 0
      flatGeometry.attributes.position.array[i], // when value = 1
      easedValue
    )
  }
  earthMesh.geometry.setAttribute('position', 
    new THREE.Float32BufferAttribute(newPositions, itemSize)
  )
}

function updateControlsBehavior() {
  controls.enableRotate = props.mode == 'globe'
  controls.enablePan = false // never use OrbitControls pan — custom handling in flat mode
  controls.enableZoom = props.mode == 'flat' // wheel/pinch zoom only in flat mode
  controls.screenSpacePanning = true

  if (props.mode == 'flat') {
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE, // disabled via enableRotate=false, won't interfere
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE,
    }
    controls.touches = {
      ONE: THREE.TOUCH.ROTATE, // disabled via enableRotate=false
      TWO: THREE.TOUCH.DOLLY_PAN, // pinch zoom works
    }
    controls.minZoom = FLAT_MIN_ZOOM
    controls.maxZoom = 8.0
  } else {
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    }
    controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_ROTATE,
    }
    controls.minZoom = 0
    controls.maxZoom = Infinity
  }
}

function updateControls() {
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)
  const globeAngleLimits = { min: Math.PI * 0.35, max: Math.PI * 0.65 } // limit "tipping"
  const flatAngleLimits = { min: 0, max: Math.PI }

  controls.minPolarAngle = THREE.MathUtils.lerp(globeAngleLimits.min, flatAngleLimits.min, easedValue)
  controls.maxPolarAngle = THREE.MathUtils.lerp(globeAngleLimits.max, flatAngleLimits.max, easedValue)
  //controls.update() is this needed here when it is also being called in the main animation loop?
}

function getFlatProjection() {
  return geoEqualEarth()
    .rotate([-centerLongitude, 0, 0])
    .scale(0.4)
    .translate([0, 0])
}

function createFlatGeometry() {
  const projection = getFlatProjection()

  const geometry = new THREE.PlaneGeometry(2, 1.04, 64, 32)

  const uvs = geometry.attributes.uv
  const positions = geometry.attributes.position

  for (let i = 0; i < uvs.count; i++) {
    const u = uvs.getX(i)
    const v = uvs.getY(i)
    const longitude = (u - 0.5) * 360
    const latitude = (0.5 - v) * 180
    const [x, y] = projection([longitude, latitude]) ?? [0, 0]
    positions.setXY(i, x, y)
  }

  return geometry
}

function rebuildFlatProjection() {
  const projection = getFlatProjection()

  const uvs = flatGeometry.attributes.uv
  const positions = flatGeometry.attributes.position

  for (let i = 0; i < uvs.count; i++) {
    const u = uvs.getX(i)
    const v = uvs.getY(i)
    const longitude = (u - 0.5) * 360
    const latitude = (0.5 - v) * 180
    const [x, y] = projection([longitude, latitude]) ?? [0, 0]
    positions.setXY(i, x, y)
  }
  positions.needsUpdate = true

  // Collapse triangles at the anti-meridian seam.
  // As centerLongitude changes, the anti-meridian moves through the mesh interior.
  // Vertices on opposite sides of the seam project to opposite edges of the oval,
  // creating triangles that stretch across the full projection width.
  const cols = 65 // 64 segments + 1
  const rows = 33 // 32 segments + 1
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols - 1; col++) {
      const i = row * cols + col
      const x1 = positions.getX(i)
      const x2 = positions.getX(i + 1)
      // Normal adjacent spacing is ~0.03; seam gap is ~2.0
      if (Math.abs(x2 - x1) > 0.5) {
        // Snap seam vertices to their non-seam neighbors to collapse the triangle
        if (col > 0) {
          positions.setXY(i, positions.getX(i - 1), positions.getY(i - 1))
        }
        if (col + 2 < cols) {
          positions.setXY(i + 1, positions.getX(i + 2), positions.getY(i + 2))
        }
        break // only one seam per row
      }
    }
  }

  // Re-morph the mesh geometry
  updateGeometry()

  // Update grid lines to match new center meridian
  updateLongitudeLinePositions()
  updateLatitudeLinePositions()

  // Update marker/flag positions
  for (const m of markers) updateMarker(m)
  for (const flag of flags.values()) updateFlagPosition(flag)
}

async function addEarthAsync() {
  const textureLoader = new THREE.TextureLoader()
  const texture = await textureLoader.loadAsync(earthTexture)

  const material = new THREE.MeshStandardMaterial({ map: texture })
  earthMesh = new THREE.Mesh(getCurrentGeometry().clone(), material)
  addLatitudeLines()
  addLongitudeLines()
  scene.add(earthMesh)
  earthLoadedRef.value = true
}

function addLatitudeLines() {
  const latitudes = [0, 30, -30, 60, -60, 66.5, -66.5];

  latitudes.forEach(lat => {
    const lineGeometry = new THREE.BufferGeometry()
    const normalOpacity = lat === 0 ? 0.3 : 0.2
    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: normalOpacity
    });

    const line = new THREE.LineLoop(lineGeometry, material);
    earthMesh.add(line);
    latitudeLines.push({ baseLat: lat, line, normalOpacity })
  });
  updateLatitudeLinePositions()
}

function addLongitudeLines() {
  for (let lon = -180; lon < 180; lon += 30) {
    const normalOpacity = lon === 0 ? 0.3 : 0.2
    const lineGeometry = new THREE.BufferGeometry()
    const material = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: normalOpacity
    });

    const line = new THREE.Line(lineGeometry, material);
    earthMesh.add(line);
    longitudeLines.push({ baseLon: lon, line, normalOpacity })
  }
  updateLongitudeLinePositions()
}

function updateLongitudeLinePositions() {
  const segments = 32
  const radius = 1.002
  const projection = getFlatProjection()
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)

  for (const ll of longitudeLines) {
    const points = []
    for (let i = 0; i <= segments; i++) {
      const phi = (i / segments) * Math.PI
      const latitude = 90 - (i / segments) * 180

      // Globe position: line at baseLon on the sphere surface
      const gx = radius * Math.sin(phi) * Math.cos(THREE.MathUtils.degToRad(ll.baseLon))
      const gy = radius * Math.cos(phi)
      const gz = radius * Math.sin(phi) * Math.sin(THREE.MathUtils.degToRad(ll.baseLon))

      // Flat position: project through Equal Earth (same projection as the map)
      const projected = projection([ll.baseLon, latitude])
      const fx = projected ? projected[0] : 0
      const fy = projected ? -projected[1] : 0 // flip y to match Three.js

      points.push(new THREE.Vector3(
        THREE.MathUtils.lerp(gx, fx, easedValue),
        THREE.MathUtils.lerp(gy, fy, easedValue),
        THREE.MathUtils.lerp(gz, 0, easedValue)
      ))
    }
    ll.line.geometry.setFromPoints(points)
  }
}

function updateLatitudeLinePositions() {
  const segments = 64
  const radius = 1.002
  const projection = getFlatProjection()
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)

  for (const ll of latitudeLines) {
    const latRad = THREE.MathUtils.degToRad(ll.baseLat)
    const r = Math.cos(latRad)
    const y = Math.sin(latRad)

    const points = []
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2

      // Globe position: circle on the sphere surface
      const gx = radius * r * Math.cos(theta)
      const gy = radius * y
      const gz = radius * r * Math.sin(theta)

      // Flat position: project through Equal Earth
      // theta in sphere coords maps to longitude: theta_degrees - 90
      const longitude = THREE.MathUtils.radToDeg(theta) - 90
      const projected = projection([longitude, ll.baseLat])
      const fx = projected ? projected[0] : 0
      const fy = projected ? -projected[1] : 0

      points.push(new THREE.Vector3(
        THREE.MathUtils.lerp(gx, fx, easedValue),
        THREE.MathUtils.lerp(gy, fy, easedValue),
        THREE.MathUtils.lerp(gz, 0, easedValue)
      ))
    }

    // Fix anti-meridian seam: collapse adjacent points with large x gap
    for (let i = 0; i < points.length - 1; i++) {
      if (Math.abs(points[i + 1].x - points[i].x) > 0.5 && easedValue > 0.5) {
        // Snap both seam points to their non-seam neighbors
        if (i > 0) points[i].copy(points[i - 1])
        if (i + 2 < points.length) points[i + 1].copy(points[i + 2])
      }
    }

    ll.line.geometry.setFromPoints(points)
  }
}

function updateGridLineOpacity() {
  // Fade grid lines out during morph transition, back in when complete
  const morphVal = morphValueRef.value
  const fadeOut = 1 - THREE.MathUtils.smoothstep(morphVal, 0, 0.15)
  const fadeIn = THREE.MathUtils.smoothstep(morphVal, 0.85, 1)
  const visibility = Math.max(fadeOut, fadeIn)

  for (const ll of longitudeLines) {
    const mat = ll.line.material as THREE.LineBasicMaterial
    mat.opacity = ll.normalOpacity * visibility
  }
  for (const ll of latitudeLines) {
    const mat = ll.line.material as THREE.LineBasicMaterial
    mat.opacity = ll.normalOpacity * visibility
  }
}

function getCurrentGeometry() {
  return props.mode == 'globe' ? globeGeometry : flatGeometry
}

function addMarker(id: string, location: LatLong) {
  const geometry = new THREE.SphereGeometry(markerRadius, 8, 8)
  const material = new THREE.MeshStandardMaterial({ color: 0xff0000 })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.visible = true
  scene.add(mesh)
  const marker = { id, location, mesh } as Marker
  markers.push(marker)
  updateMarker(marker)
}

function removeMarker(id: string) {
  const index = markers.findIndex(m => m.id == id)
  if (index >= 0) {
    const m = markers[index]
    scene.remove(m.mesh)
    markers.splice(index, 1)
  }
}

function showMarker(id: string, visible = true) {
  const m = markers.find(m => m.id == id)
  if (m) {
    m.mesh.visible = visible
  }
}

function moveToMarker(id: string) {
  const m = markers.find(m => m.id == id)
  if (m) {
    moveTo(m.location)
  }
}

function updateMarker(marker: Marker) {
  const globePosition = latLongToGlobePosition(marker.location)
  globePosition.radius = 1.05 // just above the earth surface
  
  const flatPosition = latLongToFlatPosition(marker.location)
  
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1)
  const globeVector = new THREE.Vector3().setFromSpherical(globePosition)
  
  marker.mesh.position.lerpVectors(globeVector, flatPosition, easedValue)
  marker.mesh.visible = true
}

function addCountryFlag(code: string) {
  const flagUrl = flagModules[code]
  const location = GeoUtil.getCountryLocation(code)
  if (!flagUrl || !location) {
    console.warn(`No flag or location found for country code: ${code}`)
    return
  }
  if (flags.has(code))
    return // already added

  const img = new Image()
  img.width = 100
  img.height = 75 // 4:3 aspect ratio
  img.onload = () => {
    if (flags.has(code))
      return // already loaded (load is async, so check again)

    const flagTexture = new THREE.CanvasTexture(img)
    flagTexture.colorSpace = THREE.SRGBColorSpace
    const spriteMaterial = new THREE.SpriteMaterial({ 
      map: flagTexture,
      transparent: true,
      depthTest: true,
    })

    // spriteMaterial.toneMapped = false

    const sprite = new THREE.Sprite(spriteMaterial)
    const scale = 0.08
    sprite.scale.set(scale * 1.33, scale, 1) // 4:3 aspect ratio
    scene.add(sprite)

    const flag = { code, location, sprite }
    flags.set(code, flag)

    updateFlagPosition(flag)
  }

  img.src = flagUrl // trigger img load
}

function updateFlagPosition(flag: FlagSprite) {
  const globePos = latLongToGlobePosition(flag.location);
  globePos.radius = 1.06; // Float above surface
  
  const flatPos = latLongToFlatPosition(flag.location);
  
  // Position based on current mode
  const easedValue = THREE.MathUtils.smoothstep(morphValueRef.value, 0, 1);
  const globeVector = new THREE.Vector3().setFromSpherical(globePos);
  flag.sprite.position.lerpVectors(globeVector, flatPos, easedValue);
}

function moveTo(location: LatLong) {
  // setup camera transition
  const cameraStart = new THREE.Spherical().setFromVector3(camera.position)
  const cameraTarget = latLongToGlobePosition(location)
  cameraTarget.phi = THREE.MathUtils.clamp(cameraTarget.phi, controls.minPolarAngle, controls.maxPolarAngle)
  const startTime = performance.now()
  const duration = 1000 // ms

  //enableControls.value = false // don't let user mess with camera while animating

  function animateCamera() {
    const elapsed = performance.now() - startTime

    const t = Math.min(elapsed / duration, 1.0)
    const easedT = THREE.MathUtils.smoothstep(t, 0, 1)

    // animate phi and theta separately so the transition path is consistent with manual camera control
    const newPosition = cameraStart.clone()
    newPosition.phi = THREE.MathUtils.lerp(cameraStart.phi, cameraTarget.phi, easedT)
    newPosition.theta = THREE.MathUtils.lerp(cameraStart.theta, cameraTarget.theta, easedT)
    camera.position.setFromSpherical(newPosition)
    camera.lookAt(0, 0, 0)

    if (t < 1) {
      requestAnimationFrame(animateCamera)
    }
    else {
      //enableControls.value = true // allow user to move camera again
    }
  }
  animateCamera()
}

function latLongToGlobePosition(location: LatLong) {
  // earth is centered on the origin, with north pole at the top (+y) and prime meridian at the right (+x)
  const phi = THREE.MathUtils.degToRad(90 - location.latitude) // phi goes from 0 at the top (+y) to PI at the bottom (-y)
  const theta = THREE.MathUtils.degToRad(location.longitude + 90) // theta starts at 0 at the front (+z) and goes to the right (+x)
  return new THREE.Spherical(1, phi, theta)
}

function latLongToFlatPosition(location: LatLong) {
  const projection = getFlatProjection()
  const [x, y] = projection([location.longitude, location.latitude]) ?? [0, 0]
  return new THREE.Vector3(x, -y, 0.05) // flip y to match threejs coords, slight z-offset to stay above surface
}

function clampFlatPanY() {
  const visibleHalfHeight = 1.1 / camera.zoom
  const maxOffset = Math.max(0, FLAT_MAP_HALF_HEIGHT - visibleHalfHeight)
  flatPanY = THREE.MathUtils.clamp(flatPanY, -maxOffset, maxOffset)
}

function applyFlatPanY() {
  if (props.mode == 'flat') {
    controls.target.y = flatPanY
    camera.position.y = flatPanY
  }
}

function onPointerDown(e: PointerEvent) {
  earthElemRef.value!.style.cursor = 'grabbing'

  if (props.mode == 'flat' && activePointerId == null) {
    activePointerId = e.pointerId
    lastPointerX = e.clientX
    lastPointerY = e.clientY
    earthElemRef.value!.setPointerCapture(e.pointerId)
  }
}

function onPointerMove(e: PointerEvent) {
  if (props.mode != 'flat' || activePointerId != e.pointerId) return

  const container = earthElemRef.value!
  const deltaX = e.clientX - lastPointerX
  const deltaY = e.clientY - lastPointerY
  lastPointerX = e.clientX
  lastPointerY = e.clientY

  // Horizontal: rotate center meridian (belt-sander effect)
  centerLongitude -= deltaX * (360 / container.clientWidth) / camera.zoom

  // Vertical: pan with clamping
  flatPanY += deltaY * (2.2 / container.clientHeight) / camera.zoom
  clampFlatPanY()

  rebuildFlatProjection()
  applyFlatPanY()
}

function onPointerUp(e: PointerEvent) {
  earthElemRef.value!.style.cursor = 'grab'

  if (activePointerId == e.pointerId) {
    activePointerId = null
  }
}

function toggleMode() {
  emit('update:mode', props.mode == 'globe' ? 'flat' : 'globe')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "earth",
    ref_key: "earthElemRef",
    ref: earthElemRef,
    onPointerdown: onPointerDown,
    onPointermove: onPointerMove,
    onPointerup: onPointerUp,
    onPointercancel: onPointerUp,
    onDblclick: toggleMode
  }, [
    (earthLoadedRef.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["earth-shadow", { flat: morphValueRef.value > 0 }])
        }, null, 2))
      : _createCommentVNode("", true)
  ], 544))
}
}

})